import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Heading,
  Text,
  FormControl,
  FormLabel,
  Input,
  InputGroup,
  InputRightElement,
  Icon,
  Flex,
  Button,
  useColorModeValue,
} from '@chakra-ui/react';
import { MdOutlineRemoveRedEye } from 'react-icons/md';
import { RiEyeCloseLine } from 'react-icons/ri';
import { changePasswordApi } from 'features/users/users.api';

import ConfirmationPopup from 'components/popup/ConfirmationPopup';

const ChangePassword = () => {
  const dispatch = useDispatch();
  const { userId, changePasswordStatus } = useSelector((state) => state.users);

  const bgCard = useColorModeValue('white', 'whiteAlpha.100');
  const textColor = useColorModeValue('gray.700', 'white');

  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  const [errors, setErrors] = useState({});
  const [showPasswords, setShowPasswords] = useState({
    currentPassword: false,
    newPassword: false,
    confirmPassword: false,
  });

  const [isPopupOpen, setPopupOpen] = useState(false);

  const togglePasswordVisibility = (field) => {
    setShowPasswords((prev) => ({
      ...prev,
      [field]: !prev[field],
    }));
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setPasswordData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
    setErrors((prevErrors) => ({
      ...prevErrors,
      [name]: '',
    }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!passwordData.currentPassword.trim())
      newErrors.currentPassword = 'Current password is required.';
    if (!passwordData.newPassword.trim())
      newErrors.newPassword = 'New password is required.';
    else if (passwordData.newPassword.length < 6) {
      newErrors.newPassword = 'New password must be at least 6 characters.';
    }
    if (!passwordData.confirmPassword.trim()) {
      newErrors.confirmPassword = 'Please confirm your new password.';
    } else if (passwordData.newPassword !== passwordData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match.';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChangePassword = () => {
    if (validateForm()) {
      setPopupOpen(true);
    }
  };

  const handleConfirm = () => {
    dispatch(changePasswordApi({ userId, passwordData }));
    setPopupOpen(false);
  };

  const handleClosePopup = () => {
    setPopupOpen(false);
  };

  return (
    <Box
      bg={bgCard}
      p={6}
      borderRadius="lg"
      boxShadow="sm"
      display="flex"
      flexDirection="column"
    >
      <Heading as="h3" size="lg" mb={2} color={textColor}>
        Change password
      </Heading>
      <Text fontSize="sm" color="gray.500" mb={6}>
        Here you can set your new password
      </Text>

      <FormControl isInvalid={errors.currentPassword} mb={4}>
        <FormLabel>Current Password</FormLabel>
        <InputGroup size="md">
          <Input
            type={showPasswords.currentPassword ? 'text' : 'password'}
            name="currentPassword"
            value={passwordData.currentPassword}
            onChange={handleInputChange}
            placeholder="Your current password"
            color={textColor}
          />
          <InputRightElement>
            <Icon
              as={showPasswords.currentPassword ? RiEyeCloseLine : MdOutlineRemoveRedEye}
              onClick={() => togglePasswordVisibility('currentPassword')}
              _hover={{ cursor: 'pointer' }}
              color="gray.400"
            />
          </InputRightElement>
        </InputGroup>
        {errors.currentPassword && (
          <Text color="red.500" fontSize="sm">
            {errors.currentPassword}
          </Text>
        )}
      </FormControl>

      <FormControl isInvalid={errors.newPassword} mb={4}>
        <FormLabel>New Password</FormLabel>
        <InputGroup size="md">
          <Input
            type={showPasswords.newPassword ? 'text' : 'password'}
            name="newPassword"
            value={passwordData.newPassword}
            onChange={handleInputChange}
            placeholder="Your new password"
            color={textColor}
          />
          <InputRightElement>
            <Icon
              as={showPasswords.newPassword ? RiEyeCloseLine : MdOutlineRemoveRedEye}
              onClick={() => togglePasswordVisibility('newPassword')}
              _hover={{ cursor: 'pointer' }}
              color="gray.400"
            />
          </InputRightElement>
        </InputGroup>
        {errors.newPassword && (
          <Text color="red.500" fontSize="sm">
            {errors.newPassword}
          </Text>
        )}
      </FormControl>

      <FormControl isInvalid={errors.confirmPassword} mb={4}>
        <FormLabel>New Password Confirmation</FormLabel>
        <InputGroup size="md">
          <Input
            type={showPasswords.confirmPassword ? 'text' : 'password'}
            name="confirmPassword"
            value={passwordData.confirmPassword}
            onChange={handleInputChange}
            placeholder="Confirm new password"
            color={textColor}
          />
          <InputRightElement>
            <Icon
              as={showPasswords.confirmPassword ? RiEyeCloseLine : MdOutlineRemoveRedEye}
              onClick={() => togglePasswordVisibility('confirmPassword')}
              _hover={{ cursor: 'pointer' }}
              color="gray.400"
            />
          </InputRightElement>
        </InputGroup>
        {errors.confirmPassword && (
          <Text color="red.500" fontSize="sm">
            {errors.confirmPassword}
          </Text>
        )}
      </FormControl>

      <Flex justify="flex-end" mt="auto">
        <Button
          colorScheme="blue"
          w="200px"
          onClick={handleChangePassword}
          isLoading={changePasswordStatus === 'pending'}
        >
          Change Password
        </Button>
      </Flex>

      <ConfirmationPopup
        isOpen={isPopupOpen}
        onClose={handleClosePopup}
        onConfirm={handleConfirm}
        title="Confirm Password Change"
        description="Are you sure you want to change your password?"
        confirmButtonText="Yes, Change"
        cancelButtonText="Cancel"
      />
    </Box>
  );
};

export default ChangePassword;